import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useCompanies, useCreateRecord, useRecordCandidates } from '@/api/hooks';
import { failureOf, said } from '@/api/errors';
import { useToast } from '@/ui/Toast';
import { Dialog } from '@/ui/Dialog';
import { Field, TextInput, Button } from '@/ui/primitives';
import { Select } from '@/ui/fields';
import { localIsoDate } from '@/today/dates';

export function CreateRecord({ kind, companyId, onClose }: { kind: 'company' | 'contact'; companyId?: string; onClose: () => void }) {
  const create = useCreateRecord();
  const navigate = useNavigate();
  const toast = useToast();
  const [key] = useState(() => crypto.randomUUID());
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [number, setNumber] = useState('');
  const [company, setCompany] = useState(companyId ?? '');
  const [companySearch, setCompanySearch] = useState('');
  const [from, setFrom] = useState(localIsoDate(new Date()));
  const [role, setRole] = useState('');
  const [allowSeparate, setAllowSeparate] = useState(false);
  const [queryName, setQueryName] = useState('');
  const [queryAddress, setQueryAddress] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const t = setTimeout(() => { setQueryName(name); setQueryAddress(address || website); setAllowSeparate(false); }, 250);
    return () => clearTimeout(t);
  }, [name, address, website]);
  const candidates = useRecordCandidates(kind, queryName, queryAddress);
  const companies = useCompanies(companySearch);
  const save = () => {
    if (!name.trim()) return setError('A name is required.');
    if (kind === 'contact' && company && !from) return setError('Choose the employment start date.');
    if (name !== queryName || (address || website) !== queryAddress || candidates.isFetching) return setError('The duplicate check is still running. Try again when it finishes.');
    if (candidates.isError) return setError('The duplicate check failed. Retry before saving.');
    setError(null);
    const common = { key, name: name.trim(), allowSeparate };
    create.mutate(kind === 'company'
      ? { ...common, kind, domain: address || null, website: website || null, orgNumber: number || null }
      : { ...common, kind, email: address || null, phone: phone || null, employment: company ? { companyId: company, from, role: role || null } : null }, {
      onSuccess: (r) => {
        toast({ text: `${kind === 'company' ? 'Company' : 'Contact'} added, awaiting relationship approval.` });
        onClose();
        void navigate(`/${r.kind === 'company' ? 'companies' : 'contacts'}/${r.id}`);
      },
      onError: (e) => setError(said(failureOf(e))),
    });
  };
  return <Dialog open onOpenChange={(o) => !o && !create.isPending && onClose()} kicker="Manual entry" title={`Add ${kind}`} secondary={{ label: 'Cancel', onClick: () => !create.isPending && onClose() }} primary={{ label: 'Save', onClick: save, disabled: create.isPending }}>
    <Field label={kind === 'company' ? 'Company name' : 'Contact name'}><TextInput value={name} onChange={(e) => setName(e.target.value)} maxLength={300} autoFocus /></Field>
    <Field label={kind === 'company' ? 'Domain or website (optional)' : 'Email (optional)'}><TextInput value={address} onChange={(e) => setAddress(e.target.value)} type={kind === 'contact' ? 'email' : 'text'} /></Field>
    {kind === 'company' ? <>
      <Field label="Website (optional)"><TextInput value={website} onChange={(e) => setWebsite(e.target.value)} /></Field>
      <Field label="Registration number (optional)"><TextInput value={number} onChange={(e) => setNumber(e.target.value)} /></Field>
    </> : <>
      <Field label="Phone (optional)"><TextInput value={phone} onChange={(e) => setPhone(e.target.value)} /></Field>
      <Field label="Find company"><TextInput value={companySearch} onChange={e => setCompanySearch(e.target.value)} placeholder="Search companies by name or domain" /></Field>
      <Field label="Company (optional)"><Select value={company} onChange={(e) => setCompany(e.target.value)} options={[{ value: '', label: 'No employment link yet' }, ...(company && !companies.data?.companies.some(c => c.id === company) ? [{ value: company, label: 'Selected company' }] : []), ...(companies.data?.companies ?? []).map((c) => ({ value: c.id, label: c.displayName ?? c.legalName }))]} /></Field>
      {company && <>
        <Field label="Employment starts"><TextInput type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="Role (optional)"><TextInput value={role} onChange={(e) => setRole(e.target.value)} maxLength={200} /></Field>
      </>}
    </>}
    {!!candidates.data?.candidates.length && <section aria-label="Possible duplicates" className="flex flex-col gap-2 border border-line p-3">
      <p className="m-0 text-dense font-semibold">Possible duplicates — use an existing record</p>
      {candidates.data.candidates.map((c) => <Button key={c.id} variant="quiet" onClick={() => { onClose(); void navigate(`/${kind === 'company' ? 'companies' : 'contacts'}/${c.id}`); }}>{c.name}{c.address ? ` · ${c.address}` : ''} · {c.reason}</Button>)}
      {!(kind === 'contact' && candidates.data.candidates.some((c) => c.exact)) && <label className="text-dense"><input type="checkbox" checked={allowSeparate} onChange={(e) => setAllowSeparate(e.target.checked)} /> This is a separate company or person; keep a separate record.</label>}
    </section>}
    {error && <p role="alert" className="m-0 text-dense text-danger">{error}</p>}
    <p className="m-0 text-dense text-meta">Saving records who they are. Relationship approval is a separate action.</p>
  </Dialog>;
}
